import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Globe, RefreshCw, Lock, AlertTriangle, Check, X, GripVertical } from 'lucide-react';
import ContentVideoSubtitles from './ContentVideoSubtitles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LanguageManagement = () => {
  const [registeredLanguages, setRegisteredLanguages] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [messagingApps, setMessagingApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLang, setSelectedLang] = useState(null);
  const [translationProgress, setTranslationProgress] = useState(null);
  const [savingAppsFor, setSavingAppsFor] = useState(null);

  // Fetch registered languages
  const fetchLanguages = useCallback(async () => {
    try {
      const [regRes, availRes, appsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/languages`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/languages/available`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/messaging-apps`, { withCredentials: true }),
      ]);

      setRegisteredLanguages(regRes.data.languages || []);
      setAvailableLanguages(availRes.data.languages || []);
      setMessagingApps(appsRes.data.apps || []);
    } catch (err) {
      toast.error('Failed to load languages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  const toggleAppForLanguage = async (lang, appId, enabled) => {
    const current = lang.messagingAppIds || [];
    const next = enabled ? [...current.filter(id => id !== appId), appId] : current.filter(id => id !== appId);
    await saveLanguageApps(lang, next);
  };

  const reorderAppsForLanguage = async (lang, orderedIds) => {
    await saveLanguageApps(lang, orderedIds);
  };

  const saveLanguageApps = async (lang, orderedIds) => {
    setSavingAppsFor(lang.code);
    // Optimistic update so drag feels instant
    setRegisteredLanguages(prev =>
      prev.map(l => l.code === lang.code ? { ...l, messagingAppIds: orderedIds } : l)
    );
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/languages/${lang.code}/messaging-apps`,
        { messaging_app_ids: orderedIds },
        { withCredentials: true }
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update messaging apps');
      fetchLanguages(); // fall back to server state on failure
    } finally {
      setSavingAppsFor(null);
    }
  };

  // Poll while any language translation is still processing
  useEffect(() => {
    const anyProcessing = registeredLanguages.some(l => l.translationStatus === 'processing');
    if (!anyProcessing) return;
    const timer = setInterval(fetchLanguages, 5000);
    return () => clearInterval(timer);
  }, [registeredLanguages, fetchLanguages]);

  // Add new language
  const handleAddLanguage = async () => {
    if (!selectedLang) return;
    
    setAdding(true);
    setTranslationProgress('Starting translation...');
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/languages`,
        { lang_code: selectedLang.code },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success(`${selectedLang.name} added! AI translation is running in the background — this takes a few minutes.`);
        setShowAddModal(false);
        setSelectedLang(null);
        fetchLanguages();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to add language';
      toast.error(errorMsg);
    } finally {
      setAdding(false);
      setTranslationProgress(null);
    }
  };

  // Remove language
  const handleRemoveLanguage = async (langCode, langName) => {
    if (!window.confirm(`Are you sure you want to remove ${langName}? This will delete all translations and subtitles for this language.`)) {
      return;
    }
    
    setRemoving(langCode);
    
    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/admin/languages/${langCode}`,
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success(`${langName} removed successfully`);
        fetchLanguages();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to remove language';
      toast.error(errorMsg);
    } finally {
      setRemoving(null);
    }
  };

  // Regenerate translations
  const handleRegenerateTranslations = async (langCode, langName) => {
    if (!window.confirm(`Regenerate AI translations for ${langName}? This may take a moment.`)) {
      return;
    }
    
    try {
      toast.info(`Regenerating translations for ${langName}...`);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/translations/regenerate/${langCode}`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success(`Translation regeneration for ${langName} started in the background`);
        fetchLanguages();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to regenerate translations';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Language Management
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Add or remove languages. AI will automatically translate content from English.
          </p>
        </div>
        
        <button
          onClick={() => setShowAddModal(true)}
          data-testid="add-language-button"
          disabled={availableLanguages.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          Add Language
        </button>
      </div>

      {/* Registered Languages List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <h3 className="font-medium text-slate-700">Registered Languages ({registeredLanguages.length})</h3>
        </div>
        
        <div className="divide-y divide-slate-100">
          {registeredLanguages.map((lang) => (
            <div
              key={lang.code}
              data-testid={`language-row-${lang.code}`}
              className="hover:bg-slate-50"
            >
              <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{lang.flag}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{lang.name}</span>
                    <span className="text-sm text-slate-500">({lang.nativeName})</span>
                    <span className="text-xs bg-slate-200 px-2 py-0.5 rounded uppercase">{lang.code}</span>
                    {lang.isCore && (
                      <span className="flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                        <Lock className="w-3 h-3" />
                        Core
                      </span>
                    )}
                    {lang.translationStatus === 'processing' && (
                      <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded" data-testid={`translating-badge-${lang.code}`}>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-700"></div>
                        Translating…
                      </span>
                    )}
                    {lang.translationStatus === 'partial' && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded" data-testid={`translation-partial-badge-${lang.code}`} title="Some fields could not be translated - use the regenerate button">
                        Partial translation
                      </span>
                    )}
                    {lang.translationStatus === 'error' && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded" data-testid={`translation-error-badge-${lang.code}`}>
                        Translation failed
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {!lang.isCore && (
                  <>
                    <button
                      onClick={() => handleRegenerateTranslations(lang.code, lang.name)}
                      className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Regenerate AI translations"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleRemoveLanguage(lang.code, lang.name)}
                      data-testid={`remove-language-${lang.code}`}
                      disabled={removing === lang.code}
                      className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Remove language"
                    >
                      {removing === lang.code ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </>
                )}
                {lang.isCore && (
                  <span className="text-xs text-slate-400 px-2">Protected</span>
                )}
              </div>
              </div>
              {/* Messaging Apps for this Language */}
              {messagingApps.length > 0 && (
                <div className="px-4 pb-3 -mt-1" data-testid={`language-apps-${lang.code}`}>
                  <LanguageMessagingApps
                    lang={lang}
                    allApps={messagingApps.filter(a => a.active)}
                    saving={savingAppsFor === lang.code}
                    onToggle={toggleAppForLanguage}
                    onReorder={reorderAppsForLanguage}
                  />
                </div>
              )}
            </div>
          ))}
          
          {registeredLanguages.length === 0 && (
            <div className="px-4 py-8 text-center text-slate-500">
              No languages registered yet.
            </div>
          )}
        </div>
      </div>

      {/* Add Language Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Add New Language</h3>
              <p className="text-sm text-slate-500 mt-1">
                Select a language to add. AI will automatically translate all content from English.
              </p>
            </div>
            
            <div className="px-6 py-4 max-h-80 overflow-y-auto">
              {availableLanguages.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Check className="w-12 h-12 mx-auto text-green-500 mb-2" />
                  <p>All available languages have been added!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => setSelectedLang(lang)}
                      data-testid={`available-language-${lang.code}`}
                      className={`w-full px-4 py-3 rounded-lg border-2 text-left flex items-center gap-3 transition-colors ${
                        selectedLang?.code === lang.code
                          ? 'border-slate-900 bg-slate-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <span className="text-2xl">{lang.flag}</span>
                      <div>
                        <span className="font-medium text-slate-900">{lang.name}</span>
                        <span className="text-sm text-slate-500 ml-2">({lang.nativeName})</span>
                      </div>
                      {selectedLang?.code === lang.code && (
                        <Check className="w-5 h-5 text-slate-900 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {translationProgress && (
              <div className="px-6 py-3 bg-blue-50 border-t border-blue-100">
                <div className="flex items-center gap-2 text-blue-700">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                  <span className="text-sm">{translationProgress}</span>
                </div>
              </div>
            )}
            
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedLang(null);
                }}
                disabled={adding}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLanguage}
                disabled={!selectedLang || adding}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {adding ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Add Language
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Site Video Subtitles */}
      <ContentVideoSubtitles />

      {/* Info Box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">Important Notes:</p>
            <ul className="list-disc list-inside space-y-1 text-amber-700">
              <li>Core languages (English, Korean, Japanese) cannot be removed</li>
              <li>When you add a language, AI automatically translates all content from English</li>
              <li>You can manually edit any translation after it is generated</li>
              <li>Removing a language will delete all its translations and subtitles</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Drag-to-reorder list of enabled messaging apps + click-to-add row for the rest.
 * Uses native HTML5 drag & drop so we don't pull in a new dependency.
 */
const LanguageMessagingApps = ({ lang, allApps, saving, onToggle, onReorder }) => {
  const enabledIds = lang.messagingAppIds || [];
  const byId = Object.fromEntries(allApps.map(a => [a.id, a]));
  const enabled = enabledIds.map(id => byId[id]).filter(Boolean);
  const disabled = allApps.filter(a => !enabledIds.includes(a.id));

  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const handleDragStart = (index) => (e) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers require setData for the drag to actually begin
    e.dataTransfer.setData('text/plain', String(index));
  };
  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overIndex !== index) setOverIndex(index);
  };
  const handleDragEnd = () => { setDragIndex(null); setOverIndex(null); };
  const handleDrop = (index) => (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) { handleDragEnd(); return; }
    const next = enabled.slice();
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    handleDragEnd();
    onReorder(lang, next.map(a => a.id));
  };

  return (
    <div className="text-xs">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-slate-500 mr-1">Messaging apps:</span>
        {enabled.length === 0 && disabled.length === 0 && (
          <span className="text-slate-400 italic">No active apps — add some under the Messaging Apps tab</span>
        )}
        {enabled.map((app, index) => (
          <span
            key={app.id}
            draggable={!saving}
            onDragStart={handleDragStart(index)}
            onDragOver={handleDragOver(index)}
            onDrop={handleDrop(index)}
            onDragEnd={handleDragEnd}
            data-testid={`enabled-app-${lang.code}-${app.id}`}
            className={`inline-flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-full border cursor-move transition-all select-none bg-indigo-600 text-white border-indigo-600 ${
              overIndex === index && dragIndex !== index ? 'ring-2 ring-indigo-300 ring-offset-1' : ''
            } ${dragIndex === index ? 'opacity-40' : ''} ${saving ? 'opacity-50' : ''}`}
          >
            <GripVertical className="w-3 h-3 opacity-70" />
            {app.name}
            <button
              onClick={() => onToggle(lang, app.id, false)}
              disabled={saving}
              data-testid={`toggle-app-${lang.code}-${app.id}`}
              className="ml-1 hover:bg-white/20 rounded p-0.5"
              title={`Remove ${app.name} from ${lang.name}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {disabled.map(app => (
          <button
            key={app.id}
            onClick={() => onToggle(lang, app.id, true)}
            disabled={saving}
            data-testid={`toggle-app-${lang.code}-${app.id}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-white text-slate-600 border-slate-300 hover:border-indigo-400 transition-colors disabled:opacity-50"
          >
            <Plus className="w-3 h-3" /> {app.name}
          </button>
        ))}
      </div>
      {enabled.length > 1 && (
        <p className="text-[10px] text-slate-400 mt-1 pl-16">Drag to reorder — this is the order shown on the contact form.</p>
      )}
    </div>
  );
};

export default LanguageManagement;
